import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import { Theme } from "@mui/material"
import { kitImages, itemImages } from "../../../../../helpers/image"
import { MedComplianceCode } from "../../medications/enums"
import { DeviceComplianceCode } from "../../devices/enums"
import { Kit, KitItem, KitItemsByAvailability, KitsByStatus } from '../types'
import { getSDSItemLink } from "../../../../../services/sdsService"
import { User, UserAccessLevels } from "../../../../../redux/user"
import { Medication } from "../../../../corp/medications/types"
import { Device } from "../../../../corp/devices/types"
import { Expiration, ExpirationTime, ItemType, KitItemTooltip, KitRefillStatus } from "../enums"

dayjs.extend(relativeTime)

export const getKitImage = (image: string | null) => {
  return image ? kitImages[`${image}.png`] : undefined
}

export const getItemImage = (image: string | null) => {
  return image ? itemImages[`${image}.jpg`] : null
}

export const getLastRefillDate = (kit: Kit) => {
  return dayjs(kit.lastRefillDate).format('MMM YYYY')
}

export const hasBeenRequested = (kit: Kit) => {
  if (kit.refillStatus !== 1 && kit.lastRefillRequestDate) {
    const today = dayjs()
    const requestDate = dayjs(kit.lastRefillRequestDate)
    const diff = today.diff(requestDate, 'days')

    return diff < 3
  } else {
    return false
  }
}

export const getPendingApproveLink = (kit: Kit, isCorpView: boolean) => {
  return `${isCorpView ? `/corp/practice/${kit.customer_number}` : '/practice'}/shipments/pending/${kit.id}`
}

export const getItemsByCompliance = (kitItems: KitItem[]) => {
  const itemsByCompliance: Map<number, KitItemsByAvailability> = new Map()

  kitItems.forEach((item: KitItem) => {
    const status = item.complianceStatus
    const itemsByAvailability = itemsByCompliance.get(status) ?? { available: [], discontinued: [] }

    if (item.discontinued) {
      itemsByAvailability.discontinued.push(item)
    } else {
      itemsByAvailability.available.push(item)
    }

    itemsByCompliance.set(status, itemsByAvailability)
  })

  return itemsByCompliance
}

export const sortByExpiration = (a: KitItem, b: KitItem) => {
  return (new Date(a.expirationDate)).getTime() - (new Date(b.expirationDate)).getTime()
}

export const getOrderedRefillItems = (kitItems: KitItem[]): KitItem[]  => {
  return [...kitItems].sort((a: KitItem, b: KitItem) => {
    const order = Number(a.discontinued) - Number(b.discontinued)

    return order === 0
      ? sortByExpiration(a, b)
      : order
  })
}

export const getOrderedItems = (itemsByCompliance: Map<number, KitItemsByAvailability>) => {
  const sortedByExpiration: Map<number, KitItemsByAvailability> = new Map()

  itemsByCompliance.forEach((itemsByAvailability, status) => {
    const { available, discontinued } = itemsByAvailability

    const sorted: KitItemsByAvailability = {
      available: [...available].sort(sortByExpiration),
      discontinued: [...discontinued].sort(sortByExpiration),
    }

    sortedByExpiration.set(status, sorted)
  })

  return sortedByExpiration
}

export const getDisplayItems = (kitItems: KitItem[]) => {
  /*
      compliance code 3: red
      compliance code 2: yellow
      compliance code 1: green
      compliance code 4: grey
  */
  const complianceCodeSortOrder = [3, 2, 1, 4]
  const itemsByCompliance = getItemsByCompliance(kitItems)
  const sortedItemsByCompliance = getOrderedItems(itemsByCompliance)
  const displayItems: KitItem[] = []

  complianceCodeSortOrder.forEach(status => {
    const itemsByAvailability = sortedItemsByCompliance.get(status) ?? { available: [], discontinued: [] }
    const { available, discontinued } = itemsByAvailability

    discontinued.forEach(item => displayItems.push(item))
    available.forEach(item => displayItems.push(item))
  })

  return displayItems
}

export const getSdsLink = async (customerNumber: string, itemNumber: string) => {
  const link = await getSDSItemLink(customerNumber, itemNumber)
  return link ?? null;
}

export const isOrderProcessing = (kitItem: KitItem) => {
  return kitItem.type === ItemType.Medication
    ? kitItem.complianceCode === MedComplianceCode.OrderProcessing
    : kitItem.complianceCode === DeviceComplianceCode.OrderProcessing
}

export const getShowingCount = (pageStart: number, pageEnd: number, totalItems: number) => {
  return `${pageStart + 1}-${pageEnd > totalItems ? totalItems : pageEnd} of ${totalItems}`
}

export const hasRefillApprovalAccess = (currentUser: User | null) => {
  const accessLevel = currentUser?.access_level
  return accessLevel === UserAccessLevels.Owner || accessLevel === UserAccessLevels.Admin
}

export const getExpirationColor = (status: number, theme: Theme) => {
  if (status === 2) {
    return theme.palette.warning.main
  } else if (status === 3) {
    return theme.palette.danger.main
  } else return null
}

export const getExpirationDate = (date: string) => {
  const expirationDate = new Date(date)
  const timezoneOffsetMinutes = expirationDate.getTimezoneOffset()
  expirationDate.setUTCMinutes(timezoneOffsetMinutes)

  return dayjs(expirationDate)
}

export const getExpirationMessage = (expirationDate: dayjs.Dayjs) => {
  const today = dayjs()
  const oneMonthFromNow = today.add(1, 'month')
  const isExpired = expirationDate.isBefore(today)
  const isExpiringWithinOneMonth = expirationDate.isBefore(oneMonthFromNow)

  if (isExpired) return Expiration.Expired // Past
  else if (isExpiringWithinOneMonth) return Expiration.Expiring // Present
  else return Expiration.Expires // Future  
}

export const getExpirationTimeMessage = (expirationDate: dayjs.Dayjs) => {
  const today = dayjs()
  const yesterday = today.subtract(1, 'day')
  const tomorrow = today.add(1, 'day')

  if (expirationDate.isSame(yesterday)) return ExpirationTime.Yesterday
  else if (expirationDate.isSame(today)) return ExpirationTime.Today
  else if (expirationDate.isSame(tomorrow)) return ExpirationTime.Tomorrow
  else return expirationDate.fromNow()
}

export const getSelectedItems = (items: KitItem[]) => {
  const selected = new Map<number, KitItem>()

  items.forEach((item: KitItem) => {
    selected.set(item.id, item)
  })

  return selected
}

export const getRefillRowColor = (item: KitItem) => {
  if (item.discontinued) return 'rgba(100, 100, 100, 0.05)'
  else if (item.isExpired) return 'rgba(255, 0, 0, 0.05)'
  else {
    const expirationDate = getExpirationDate(item.expirationDate)
    const expirationMessage = getExpirationMessage(expirationDate)

    if (expirationMessage === Expiration.Expiring) {
      return 'rgba(255, 248, 1, 0.05)'
    } else {
      return null
    }
  }
}

export const getKitsByStatus = (kits: Map<number, Kit>) => {
  const kitsByStatus: KitsByStatus = {
    active: [],
    inactive: [],
  }

  kits.forEach((kit, _) => {
    if (kit.complianceStatus === 4) {
      kitsByStatus.inactive.push(kit)
    } else {
      kitsByStatus.active.push(kit)
    }
  })

  return kitsByStatus
}

export const getKit = (item: Medication | Device) => {
  const kit: Kit = {
    id: item.kit_id,
    customer_number: item.customer_number,
    active: item.kit_active,
    type: item.kit_type,
    serialNumber: item.kit_serial_number,
    refillStatus: item.kit_refill_status,
    location: item.location,
    lastRefillDate: item.last_refill_date,
    pendingApproval: item.pending_approval,
    model: item.kit_model,
    image: item.kit_image ? item.kit_image : null,
    complianceStatus: item.kit_compliance_status,
    complianceCode: item.kit_compliance_code,
    messageShort: item.kit_message_short,
    messageLong: item.kit_message_long,
    lastRefillRequestDate: item.last_request_date,
  }

  return kit
}

export const getKitsById = (items: (Medication | Device)[]) => {
  const kits = new Map<number, Kit>()

  items.forEach(item => {
    const kitId = item.kit_id

    if (kits.has(kitId) === false) {
      kits.set(kitId, getKit(item))
    }
  })

  return kits
}

export const getKitItem = (item: Medication | Device) => {
  const today = dayjs()

  const id = isMedication(item) ? item.medication_id : item.device_id
  const sds = isMedication(item) ? item.is_sds : false
  const type = isMedication(item) ? ItemType.Medication : ItemType.Device

  const name = isMedication(item)
    ? Boolean(item.item_description) ? item.item_description : 'N/A'
    : item.item_description

  const expirationDate = getExpirationDate(item.expiration_date)
  const isExpired = expirationDate.isBefore(today)    

  const kitItem: KitItem = {
    id,
    kit_id: item.kit_id,
    active: item.item_active,
    itemNumber: item.item_number,
    lotNumber: item.lot_number,
    quantity: item.quantity,
    expirationDate: item.expiration_date,
    onHold: item.on_hold,
    refillStatus: item.item_refill_status,
    status: item.item_status,
    complianceStatus: item.item_compliance_status,
    complianceCode: item.item_compliance_code,
    messageShort: item.item_message_short,
    messageLong: item.item_message_long,
    name,
    image: item.item_image ? item.item_image : null,
    refillable: item.refillable,
    discontinued: item.is_discontinued,
    nextRefillDate: item.next_refill_date,
    sds,
    type,
    isExpired,
    isBackordered: item.is_backordered,
  }

  return kitItem
}

export const getKitItemsById = (items: (Medication | Device)[]) => {
  const kitItemsById: Map<number, KitItem[]> = new Map()

  items.forEach(item => {
    const kitItem = getKitItem(item)
    const kitId = item.kit_id

    kitItemsById.get(kitId)?.push(kitItem)
      ?? kitItemsById.set(kitId, [kitItem])
  })

  return kitItemsById
}

export const getRefillStatus = (kitItem: KitItem) => {
  if (kitItem.active && kitItem.discontinued) {
    return KitRefillStatus.discontinued
  } else if (kitItem.active && !kitItem.discontinued && kitItem.refillable) {
    return KitRefillStatus.refillOn
  } else if (!kitItem.active || !kitItem.discontinued && !kitItem.refillable) {
    return KitRefillStatus.refillOff
  }
}

export const getTooltipTitle = (refillStatus: string) => {
  if (refillStatus === KitRefillStatus.discontinued) {
    return KitItemTooltip.discontinued
  } else if (refillStatus === KitRefillStatus.refillOn) {
    return KitItemTooltip.refillOn
  } else if (refillStatus === KitRefillStatus.refillOff) {
    return KitItemTooltip.refillOff
  }
}

export const getOrderedKits = (kits: (Kit)[]) => {
  return [...kits].sort((a: Kit, b: Kit) => a.complianceStatus - b.complianceStatus)
}

export const isMedication = (item: Medication | Device): item is Medication => {
  return (item as Medication).is_sds !== undefined
}


